/**
 * Shared region checkbox list — extracted from CoverageRegionSettings
 * for reuse in both the settings page and the onboarding wizard (V92-6-4).
 *
 * Each region is a selectable card with label and optional hint.
 * Uses design-system tokens for all colors/spacing.
 */
import type { CoverageRegion } from '../lib/api';

export const REGIONS: { code: CoverageRegion; label: string; hint?: string }[] = [
  { code: 'US', label: 'United States' },
  { code: 'EU', label: 'European Union / Europe' },
  { code: 'APAC', label: 'Asia-Pacific' },
  { code: 'LATAM', label: 'Latin America' },
  { code: 'MENA', label: 'Middle East & North Africa' },
  { code: 'SSA', label: 'Sub-Saharan Africa' },
  { code: 'GLOBAL', label: 'Multilateral / cross-border bodies', hint: 'G7, OECD, UN, and other advisory instruments — not "all regions."' },
];

interface RegionCheckboxListProps {
  selected: Set<CoverageRegion>;
  onToggle: (code: CoverageRegion) => void;
  disabled?: boolean;
  legend?: string;
}

export function RegionCheckboxList({ selected, onToggle, disabled, legend }: RegionCheckboxListProps) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
      <legend
        style={{
          padding: 0,
          marginBottom: 'var(--space-1)',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        {legend ?? 'Select the regions this workspace operates in'}
      </legend>
      {REGIONS.map((r) => {
        const on = selected.has(r.code);
        return (
          <label
            key={r.code}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              background: on ? 'var(--surface-elevated)' : 'var(--surface)',
              padding: 'var(--space-3) var(--space-4)',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={on}
              disabled={disabled}
              onChange={() => onToggle(r.code)}
              style={{ marginTop: '3px' }}
            />
            <span style={{ display: 'grid', gap: '2px' }}>
              <span style={{ color: 'var(--text)', fontSize: 'var(--font-size-md)' }}>{r.label}</span>
              {r.hint ? (
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>{r.hint}</span>
              ) : null}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
